import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    IconButton,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { useTranslation } from 'react-i18next';

import useNotify from '../../../hooks/useNotify';
import {
    useGetUpgradePaths,
    createUpgradePath,
    deleteUpgradePath,
} from '@openapi/upgrade-offers/upgrade-offers';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';

function UpgradePathsSection({ showId }) {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();

    const [fromTicketTypeId, setFromTicketTypeId] = useState('');
    const [toTicketTypeId, setToTicketTypeId] = useState('');

    // Load existing upgrade paths
    const {
        data: pathsData,
        isLoading: pathsLoading,
        refetch: refetchPaths,
    } = useGetUpgradePaths(showId, {
        query: { enabled: !!showId },
    });

    // Load ticket types for this show
    const { data: ticketTypesData, isLoading: ticketTypesLoading } = useGetTicketTypes(
        { optionalId: Number(showId), page: 0, pageSize: 1000 },
        { query: { enabled: !!showId } },
    );

    const [adding, setAdding] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const paths = pathsData?.paths ?? [];
    const ticketTypes = ticketTypesData?.ticketTypes ?? [];

    const usedFromTicketTypeIds = new Set(paths.map((p) => p.fromTicketTypeId));
    const fromOptions = ticketTypes.filter(
        (tt) => !usedFromTicketTypeIds.has(tt.id) && tt.id !== toTicketTypeId,
    );
    const toOptions = ticketTypes.filter((tt) => tt.id !== fromTicketTypeId);

    const handleAdd = async () => {
        if (!fromTicketTypeId || !toTicketTypeId) return;
        setAdding(true);
        try {
            await createUpgradePath(Number(showId), {
                fromTicketTypeId: Number(fromTicketTypeId),
                toTicketTypeId: Number(toTicketTypeId),
            });
            await refetchPaths();
            notifySuccess(t('upgrades.pathAdded'));
            setFromTicketTypeId('');
            setToTicketTypeId('');
        } catch (err) {
            notifyError(err);
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (upgradePathId) => {
        setDeletingId(upgradePathId);
        try {
            await deleteUpgradePath(Number(showId), upgradePathId);
            await refetchPaths();
            notifySuccess(t('upgrades.pathRemoved'));
        } catch (err) {
            notifyError(err);
        } finally {
            setDeletingId(null);
        }
    };

    if (pathsLoading || ticketTypesLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ py: 2, width: '100%', minWidth: 0 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'left' }}>
                {t('upgrades.upgradePaths')}
            </Typography>

            <TableContainer>
                <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '40%' }}>
                                {t('upgrades.fromTicketType')}
                            </TableCell>
                            <TableCell sx={{ width: '40%' }}>
                                {t('upgrades.toTicketType')}
                            </TableCell>
                            <TableCell align="right" sx={{ width: 140 }}>
                                {t('common.actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paths.map((path) => (
                            <TableRow key={path.id}>
                                <TableCell>
                                    {path.fromTicketTypeName ?? path.fromTicketTypeId}
                                </TableCell>
                                <TableCell>
                                    {path.toTicketTypeName ?? path.toTicketTypeId}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(path.id)}
                                        disabled={deletingId === path.id}
                                        title={t('upgrades.removePath')}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}

                        <TableRow>
                            {[
                                {
                                    labelKey: 'upgrades.fromTicketType',
                                    value: fromTicketTypeId,
                                    set: setFromTicketTypeId,
                                    options: fromOptions,
                                },
                                {
                                    labelKey: 'upgrades.toTicketType',
                                    value: toTicketTypeId,
                                    set: setToTicketTypeId,
                                    options: toOptions,
                                },
                            ].map(({ labelKey, value, set, options }) => (
                                <TableCell key={labelKey}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>{t(labelKey)}</InputLabel>
                                        <Select
                                            value={value}
                                            onChange={(e) => set(e.target.value)}
                                            label={t(labelKey)}
                                        >
                                            {options.map((tt) => (
                                                <MenuItem key={tt.id} value={tt.id}>
                                                    {tt.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            ))}
                            <TableCell align="right">
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleAdd}
                                    disabled={!fromTicketTypeId || !toTicketTypeId || adding}
                                >
                                    {t('upgrades.addPath')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default UpgradePathsSection;
